import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/userService';
import { adminService } from '../services/adminService';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import Select from '../components/common/Select';
import Loading from '../components/common/Loading';

const MyTeamPage = () => {
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [teamRes, usersRes] = await Promise.all([
        userService.getDirectReports(user.id),
        userService.getAllUsers()
      ]);

      const team = teamRes.data || [];
      const users = usersRes.data || [];

      setTeamMembers(team);
      setAvailableUsers(users.filter(u =>
        (!u.managerId || u.managerId !== user.id) && u.id !== user.id
      ));
    } catch (error) {
      console.error('Failed to load team:', error);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAssignUser = async (e) => {
    e.preventDefault();
    
    if (!selectedUserId) {
      alert('Please select a user');
      return;
    }

    try {
      await adminService.assignManager(selectedUserId, user.id);
      
      setShowModal(false);
      setSelectedUserId('');
      
      alert('User added to your team successfully!');
      loadData();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to assign user');
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user from your team?')) {
      return;
    }

    try {
      await adminService.assignManager(userId, null);
      alert('User removed from your team');
      loadData();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to remove user');
    }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">My Team</h1>
          <p className="text-gray-600">Manage your direct reports</p>
        </div>
        <Button onClick={() => setShowModal(true)}>Add Team Member</Button>
      </div>

      {teamMembers.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="mb-4">No team members yet.</p>
          <p className="text-sm mb-4">Add existing users to your team.</p>
          <Button onClick={() => setShowModal(true)}>Add Your First Team Member</Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Roles</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {teamMembers.map(member => (
                <tr key={member.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {member.fullName || `${member.firstName} ${member.lastName}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{member.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {member.department?.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-1">
                      {member.roles && member.roles.map(role => (
                        <span key={role} className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                          {role}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded ${
                      member.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {member.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRemoveUser(member.id)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add Team Member">
        <form onSubmit={handleAssignUser}>
          <Select
            label="Select User"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            options={availableUsers.map(u => ({ 
              value: u.id, 
              label: `${u.firstName} ${u.lastName} (${u.email})` 
            }))}
            placeholder="Choose a user to add to your team"
            required
          />

          {availableUsers.length === 0 && (
            <div className="mt-4 p-4 bg-yellow-50 rounded">
              <p className="text-sm text-yellow-800">
                <strong>No available users.</strong> All users are already assigned to teams.
                Ask an Admin to create new users.
              </p>
            </div>
          )}

          {availableUsers.length > 0 && (
            <div className="mt-4 p-4 bg-blue-50 rounded">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This will assign the selected user as your direct report.
                Only users without a manager or from other teams are shown.
              </p>
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary"
              disabled={availableUsers.length === 0}
            >
              Add to Team
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default MyTeamPage;